import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Login from './pages/login';
import Register from './pages/register';
import ProfilePage from './pages/profile';
import UsersPage from './pages/users/index';
import DashboardPage from './pages/dashboard';
import LeadsPage from './pages/leads';
import LeadDetailPage from './pages/leads/detail';
import PurchasesPage from './pages/purchases';
import PurchaseDetailPage from './pages/purchases/detail';
import InvoicePage from './pages/purchases/invoice';
import StripeTransactions from './pages/admin/Stripe/StripeTransactions';
import PromoCodesPage from './pages/admin/promo-codes';
import AppLayout from './components/AppLayout';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CartProvider } from './contexts/CartContext';
import CheckoutPage from './pages/checkout';
import InstallPWA from './components/InstallPWA';
import ScrollToTop from './components/ScrollToTop';
import ReturnsPage from './pages/admin/returns';
import UserReturnsPage from './pages/returns';
import NotFound from './pages/NotFound';
import TermsPage from './pages/terms';
import TermsOfServicePage from './pages/terms-of-service';

// PrivateRoute Component with Layout
const PrivateRoute = ({ element: Element, isAuthenticated, requiredRole, ...rest }) => {
  const location = useLocation();
  const { currentUser } = useSelector((state) => state.user);
  const userRole = currentUser?.role;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ prevUrl: location.pathname + location.search }} replace />;
  }

  // If route requires admin role and user is not admin
  if (requiredRole === 'admin' && userRole !== 'admin') {
    return <Navigate to="/leads" replace />;
  }

  // If user is not admin and trying to access dashboard, show 404
  if (userRole !== 'admin' && location.pathname === '/dashboard') {
    return <NotFound />;
  }

  return (
    <AppLayout>
      {Element}
    </AppLayout>
  );
};

// PublicRoute Component
const PublicRoute = ({ element: Element, isAuthenticated, ...rest }) => {
  const { currentUser } = useSelector((state) => state.user);
  const userRole = currentUser?.role;

  if (isAuthenticated) {
    // Redirect admin to dashboard, regular users to leads
    return userRole === 'admin' ? 
      <Navigate to="/dashboard" replace /> : 
      <Navigate to="/leads" replace />;
  }
  return Element;
};

const queryClient = new QueryClient();

function App() {
  const { currentUser } = useSelector((state) => state.user);
  const isAuthenticated = currentUser?.token;

  return (
    <CartProvider>
      <QueryClientProvider client={queryClient}>
        <div className="App">
          <Router>
            <ScrollToTop />
            <Routes>
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute
                    isAuthenticated={isAuthenticated}
                    element={<Login />}
                  />
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute
                    isAuthenticated={isAuthenticated}
                    element={<Register />}
                  />
                }
              />
              
              <Route
                path="/terms"
                element={<TermsPage />}
              />
              
              <Route
                path="/terms-of-service"
                element={<TermsOfServicePage />}
              />
              
              {/* Redirect root based on role */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    currentUser?.role === 'admin' ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <Navigate to="/leads" replace />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />

              {/* Protected Routes */}
              <Route
                path="/profile"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    element={<ProfilePage />}
                  />
                }
              />
              
              {/* Admin Routes */}
              <Route
                path="/admin/users"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    requiredRole="admin"
                    element={<UsersPage />}
                  />
                }
              />

              <Route
                path="/admin/stripe-transactions"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    requiredRole="admin"
                    element={<StripeTransactions />}
                  />
                }
              />

              <Route
                path="/admin/leads"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    requiredRole="admin"
                    element={<LeadsPage />}
                  />
                }
              />

              <Route
                path="/admin/leads/:id"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    requiredRole="admin"
                    element={<LeadDetailPage />}
                  />
                }
              />

              <Route
                path="/admin/returns"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    requiredRole="admin"
                    element={<ReturnsPage />}
                  />
                }
              />

              <Route
                path="/admin/promo-codes"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    requiredRole="admin"
                    element={<PromoCodesPage />}
                  />
                }
              />

              {/* User Routes */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    element={<DashboardPage />}
                  />
                }
              />

              <Route
                path="/leads"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    element={<LeadsPage />}
                  />
                }
              />

              <Route
                path="/leads/:id"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    element={<LeadDetailPage />}
                  />
                }
              />

              <Route
                path="/purchases"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    element={<PurchasesPage />}
                  />
                }
              />

              <Route
                path="/purchases/:id"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    element={<PurchaseDetailPage />}
                  />
                }
              />

              <Route
                path="/purchases/:id/invoice"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    element={<InvoicePage />}
                  />
                }
              />

              <Route
                path="/returns"
                element={
                  <PrivateRoute
                    isAuthenticated={isAuthenticated}
                    element={<UserReturnsPage />}
                  />
                }
              />

              <Route path="/checkout" element={<CheckoutPage />} />

              {/* Catch-all route for 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
          <InstallPWA />
        </div>
      </QueryClientProvider>
    </CartProvider>
  );
}

export default App;
